import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getAuthUser } from '@/lib/authUser';
import { useLiveReleases } from '@/hooks/useFeatureAccess';

/**
 * May this visitor start a free trial? Only once the trial is released
 * (Admin › Releases › free trial set to Live): until then live checkouts
 * start paid, so no page offers it. Once live, a visitor without an account
 * may (the account they create has never paid); a signed-in account may until
 * it has paid once or started a trial (accounts.first_paid_at / trial_used_at).
 * create-checkout makes the same checks and has the last word.
 */
export function useTrialReleased(): boolean {
  const { data } = useLiveReleases();
  return data?.has('free_trial') ?? false;
}

export function useTrialEligible(): boolean {
  const released = useTrialReleased();
  const { data } = useQuery({
    queryKey: ['trial-eligible'],
    staleTime: 5 * 60 * 1000,
    enabled: released,
    queryFn: async (): Promise<boolean> => {
      const { data: { user } } = await getAuthUser();
      if (!user) return true;
      const { data: au } = await supabase.from('account_users').select('account_id').eq('user_id', user.id).maybeSingle();
      if (!au?.account_id) return true;
      const full = await supabase.from('accounts').select('first_paid_at, trial_used_at').eq('id', au.account_id).maybeSingle();
      // Before the trial migration there is no trial_used_at: read what there is.
      const row = (full.error
        ? (await supabase.from('accounts').select('first_paid_at').eq('id', au.account_id).maybeSingle()).data
        : full.data) as { first_paid_at?: string | null; trial_used_at?: string | null } | null;
      return !row?.first_paid_at && !row?.trial_used_at;
    },
  });
  return released && (data ?? true);
}
